import {FC, useEffect, useMemo, useState} from 'react';
//store
import {useAppSelector} from '@/store/hooks';
import {getSponsorCategoriesSelector} from '@/store/organization-event';
//mui
import {Box, Typography} from '@mui/material';
//formik
import {useFormikContext} from 'formik';
import {CreateEventFormValues, EventSponsor} from '../../../formConfiguration';
//helpers
import {getImagePreview} from '@/utils/helpers';
// import groupSponsorsByCategory from './helpers';
//constants
import {
  BODY1,
  CANCEL,
  DELETE,
  DELETE_SPONSOR,
  EDIT,
  ERROR,
  FIELD,
  IMAGE_PREVIEW_ALT,
  SUBTITLE2,
} from '@/utils/constants';
//icons
import {ReactComponent as More} from '@icons/more-vertical.svg';
import {ReactComponent as DangerIcon} from '@icons/danger.svg';
import TestImage from '@/assets/images/test-table-image.png';
//components
import AddSponsorForm from '../AddSponsorModalForm';
import Modal from '@/shared/components/Modal';
import Dropdown from '@/shared/components/Dropdown';
//styles
import {sponsorsListStyle} from './SponsorsList.styles';
import {getTeamSelector} from '@/store/organization';
import {ITeamRole} from '@/utils/enums';

interface SponsorsListProps {
  isAddModalOpen: boolean;
  onAddModalChange: () => void;
  isEditMode?: boolean;
}

const SponsorsList: FC<SponsorsListProps> = ({isAddModalOpen, onAddModalChange, isEditMode}) => {
  const {
    setFieldValue,
    values: {sponsors},
  } = useFormikContext<CreateEventFormValues>();

  const sponsorCategories = useAppSelector(getSponsorCategoriesSelector);
  // const event = useSelector(getCurrentEventSelector);
  const team = useAppSelector(getTeamSelector);

  const isStandartMember = !!team && team.role === ITeamRole.STANDART;

  const [isDropdownActionOpen, setIsDropdownActionOpen] = useState<string | number | null | undefined>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const [targetSponsorId, setTargetSponsorId] = useState<string | null | number>(null);
  const [editableSponsor, setEditableSponsor] = useState<EventSponsor | null>(null);
  const UNCATEGORIZED = 'Uncategorized';

  useEffect(() => {
    if (!isAddModalOpen) setEditableSponsor(null);
  }, [isAddModalOpen]);

  const handleDropdownButtonOpen = (uniqueId?: string | number | null) => () => {
    if (uniqueId) setTargetSponsorId(uniqueId);
    setIsDropdownActionOpen(isDropdownActionOpen === uniqueId ? null : uniqueId);
  };

  const handleDeleteModalOpen = () => setIsDeleteModalOpen(!isDeleteModalOpen);

  const handleDeleteModalAccept = () => {
    const filteredSponsors = sponsors?.filter((sponsor) => sponsor.id !== targetSponsorId);
    setFieldValue(FIELD.sponsors, filteredSponsors);
    handleDeleteModalOpen();
  };

  const handleEditSponsor = (uniqueId: string | number) => () => {
    const editableSponsor = sponsors?.find((sponsor) => sponsor.id === uniqueId);
    setEditableSponsor(editableSponsor as EventSponsor);
    onAddModalChange();
  };

  const handleRemoveSponsor = (uniqueId: string | number) => {
    handleDeleteModalOpen();
    setTargetSponsorId(uniqueId);
    handleDropdownButtonOpen();
  };

  const groupSponsorsByCategory = (sponsors: EventSponsor[] | null) =>
    (sponsors || []).reduce<Record<string, EventSponsor[]>>((acc, sponsor) => {
      const key = sponsor.category?.name.toLowerCase() || UNCATEGORIZED;
      if (!acc[key]) acc[key] = [];
      acc[key].push(sponsor);
      return acc;
    }, {});

  const groupedSponsors = groupSponsorsByCategory(sponsors);

  const sortedCategoryNames = useMemo(() => {
    const knownOrder = new Map((sponsorCategories || []).map((c) => [c.name, c.sortOrder]));
    return Object.keys(groupedSponsors).sort(
      (a, b) => (knownOrder.get(a) ?? Infinity) - (knownOrder.get(b) ?? Infinity)
    );
  }, [groupedSponsors, sponsorCategories]);

  return (
    <Box>
      {sortedCategoryNames.map((categoryName) => (
        <Box key={categoryName}>
          <Typography variant={SUBTITLE2} sx={sponsorsListStyle.title}>
            {categoryName}
          </Typography>
          <Box sx={sponsorsListStyle.content}>
            {groupedSponsors[categoryName].map((item: EventSponsor) => (
              <Box
                key={item.id}
                sx={{
                  ...sponsorsListStyle.sponsor,
                  ...(isStandartMember && {pointerEvents: 'none', cursor: 'default', opacity: 0.5}),
                }}
              >
                <Box sx={sponsorsListStyle.sponsorImage}>
                  <img src={getImagePreview(item.logo || TestImage)} alt={IMAGE_PREVIEW_ALT} />
                </Box>

                <Box key={item.id} sx={sponsorsListStyle.sponsorName}>
                  {item.name}
                </Box>

                <Dropdown
                  parentClassName={sponsorsListStyle.dropdown}
                  toggleContent={
                    <Box
                      sx={{
                        ...(targetSponsorId === item.id && sponsorsListStyle.dropdownIcon),
                      }}
                    >
                      <More />
                    </Box>
                  }
                  options={
                    <Box>
                      <Typography
                        variant={BODY1}
                        sx={sponsorsListStyle.dropdownOption}
                        onClick={handleEditSponsor(item.id)}
                      >
                        {EDIT}
                      </Typography>
                      <Typography
                        variant={BODY1}
                        sx={sponsorsListStyle.dropdownOption}
                        onClick={() => (isEditMode ? handleRemoveSponsor(item.id) : handleDeleteModalOpen())}
                      >
                        {DELETE}
                      </Typography>
                    </Box>
                  }
                  open={isDropdownActionOpen === item.id}
                  onOpen={handleDropdownButtonOpen(item.id)}
                  onClose={handleDropdownButtonOpen(null)}
                />
              </Box>
            ))}
          </Box>
        </Box>
      ))}

      <Modal
        title={DELETE_SPONSOR}
        description="Are you sure you want to delete the sponsor?"
        closeButtonTitle={CANCEL}
        mainButtonTitle={DELETE}
        onClose={handleDeleteModalOpen}
        onSave={handleDeleteModalAccept}
        open={isDeleteModalOpen}
        mainIcon={<DangerIcon />}
        sx={sponsorsListStyle.modal}
        color={ERROR}
        isShowCloseButton
        isShowSaveButton
      />

      {isAddModalOpen && (
        <AddSponsorForm onClose={onAddModalChange} editableSponsor={editableSponsor} isEditMode={isEditMode} />
      )}
    </Box>
  );
};

export default SponsorsList;
